import React, { useState, useEffect } from 'react';
import { ArrowRight, PlayCircle } from 'lucide-react';
import content from '../../translations/content.json';
import { HeroGraphic } from '../HeroGraphic/HeroGraphic';
import './style.css';

const HeroBackgroundWave = () => (
  <svg
    className="hero-background-wave"
    viewBox="0 0 1000 600"
    preserveAspectRatio="none"
  >
    <defs>
      <radialGradient id="waveGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
        <stop offset="0%" stopColor="#A855F7" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#EC4899" stopOpacity="0" />
      </radialGradient>
    </defs>
    <rect width="1000" height="600" fill="url(#waveGradient)" />
    <path
      d="M0,100 C150,200 350,0 500,100 S850,200 1000,100 V600 H0 Z" // Simplified path 1
      fill="#EC4899"
      opacity="0.05"
    />
    <path
      d="M0,200 C150,100 350,300 500,200 S850,100 1000,200 V600 H0 Z" // Simplified path 2
      fill="#A855F7"
      opacity="0.05"
    />
  </svg>
);

// Local TypingText component for Hero Subheading
const HeroTypingText = ({ text }) => {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    setDisplayedText('');
    let i = 0;
    const typingInterval = setInterval(() => {
      i++;
      setDisplayedText(text.substring(0, i));
      if (i >= text.length) {
        clearInterval(typingInterval);
      }
    }, 20); // Typing speed

    return () => clearInterval(typingInterval);
  }, [text]);

  return <p className="hero-subheading">{displayedText}</p>;
};


export const HeroSection = () => {
  const { hero } = content;
  const areCtaButtonsEnabled = false;

  // Use content from content.json for the heading
  const fullTextHeading = hero.heading.highlight;
  const [displayedTextHeading, setDisplayedTextHeading] = useState("");

  useEffect(() => {
    setDisplayedTextHeading("");
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayedTextHeading(fullTextHeading.substring(0, i));
      if (i >= fullTextHeading.length) {
        clearInterval(interval);
      }
    }, 70); // Typing speed

    return () => clearInterval(interval);
  }, [fullTextHeading]);

  return (
    <section className="hero-section">
      <HeroBackgroundWave />

      {/* Floating Gradient Orbs Container */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute top-10 left-1/4 w-72 h-72 bg-purple-300/50 rounded-full blur-3xl animate-blob-pulse" />
        <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-pink-300/50 rounded-full blur-3xl animate-blob-pulse-8s" />
        <div className="absolute top-1/2 right-10 w-64 h-64 bg-orange-200/40 rounded-full blur-3xl animate-blob-bounce" />
      </div>

      <div className="hero-content-container">
        <div className="hero-grid">
          <div className="hero-text-column">
            {/* Top Pill Badge Gradient Border */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-orange-300/60 bg-gradient-to-r from-purple-50/50 via-pink-50/50 to-orange-50/50 shadow-sm text-xs font-semibold text-slate-800 mb-4">
              <span className="text-purple-600">★</span> {hero.badge}
            </div>

            {/* Heading with Typewriter (only on load) */}
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-6"
            >
              <span className="block animate-fade-in-down">{hero.heading.prefix}</span>
              <span className="bg-gradient-to-r from-[#5E17EB] via-[#EC4899] to-[#FF6B00] bg-clip-text text-transparent">
                {displayedTextHeading}
              </span>
            </h1>

            {/* Subheading with re-typing on hover */}
            <div>
              <HeroTypingText text={hero.subheading} />
            </div>

            <div className="hero-cta-container">
              {/* Primary CTA Button Gradient */}
              <a
                href="#demo"
                className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl font-bold text-white bg-gradient-to-r from-[#5E17EB] via-[#EC4899] to-[#FF6B00] shadow-lg shadow-purple-500/25 hover:opacity-95 transition-all gap-2"
              >
                {hero.cta.requestDemo} <ArrowRight className="w-4 h-4"/>
              </a>
              <button
                className="hero-cta-secondary"
                disabled={!areCtaButtonsEnabled}
              >
                <PlayCircle className="mr-2 h-5 w-5 text-slate-500" />
                {hero.cta.watchVideo}
              </button>
            </div>
          </div>

          {/* HeroGraphic component */}
          <div className="hero-image-column relative">
            {/* Subtle Rotating Ring */}
            <div className="absolute inset-0 flex items-center justify-center z-0">
              <div className="w-[calc(100%+40px)] h-[calc(100%+40px)] rounded-full border border-purple-400/30 animate-scramble-ring-rotate"></div>
            </div>
            <HeroGraphic />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
